import {
   Box,
   Grid,
   Typography,
   TextField
} from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FixedSizeList as List, areEqual } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import AutoSizer from "react-virtualized-auto-sizer";
import dayjs from "dayjs";
import { sizes } from "../setupUtils";

export default function EditIndividualCustomerDate({ searchQuery, tempCustomers, setTempCustomers, loadMore }) {
   const { selCustomers, editFromDate, editToDate } = useSelector((store) => store.customerGroupSetup);
   const minDate = dayjs().add(1, "day");
   
   const filteredCustomers = useMemo(() => {
      if (!searchQuery) return tempCustomers;
      const lowercasedQuery = searchQuery.toLowerCase();
      return tempCustomers.filter((customer) => {
         const customerName = customer.customerName || "";
         const clientCustomerCode = customer.clientCustomerCode || "";
         return (
            customerName.toLowerCase().includes(lowercasedQuery) ||
            clientCustomerCode.toLowerCase().includes(lowercasedQuery)
         );
      });
   }, [searchQuery, tempCustomers]);

   const updateFromDate = useCallback((date, customerID) => {
      setTempCustomers((prev) => prev.map((item) =>
         item.customerID === customerID
            ? { ...item, effectiveStartDate: dayjs(date).startOf('day') }
            : item
      ));
   }, []);

   const updateToDate = useCallback((date, customerID) => {
      setTempCustomers((prev) => prev.map((item) =>
         item.customerID === customerID
            ? { ...item, effectiveEndDate: dayjs(date).startOf('day') }
            : item
      ));
   }, []);

   const Row = React.memo(({ index, style, data }) => {
      const customer = data[index];
      if (!customer) return null;
     
      const isDisabled = dayjs(customer.effectiveStartDate).startOf('day').isBefore(dayjs());
      return (
         <div style={{ ...style, display: "flex", alignItems: "center", padding: "5px 10px" }}>
            <Grid container columnSpacing={1} alignItems="center">
               <Grid item xs={8}>
                  <Typography variant="body1">{customer.customerName}</Typography>
               </Grid>
               <Grid item xs={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                     <DatePicker
                        minDate={minDate}
                        label="From"
                        value={dayjs(customer.effectiveStartDate || editFromDate).startOf('day')}
                        format="DD/MM/YYYY"
                        disabled={isDisabled}
                        onChange={(newDate) => updateFromDate(newDate, customer.customerID)}
                     />
                  </LocalizationProvider>
               </Grid>
               <Grid item xs={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                     <DatePicker
                        minDate={minDate}
                        label="To"
                        value={dayjs(customer.effectiveEndDate || editToDate).startOf('day')}
                        format="DD/MM/YYYY"
                        onChange={(newDate) => updateToDate(newDate, customer.customerID)}
                     />
                  </LocalizationProvider>
               </Grid>
            </Grid>
         </div>
      );
   }, areEqual);

   const isItemLoaded = (index) =>  !!filteredCustomers[index];
   const loadMoreItems = loadMore;

   return (
      <Box sx={{ mt: 0 }}>
         <Box sx={{ bgcolor: "#EEF2F4", height: 40, pl: 1, pt: 1, borderRadius: 1 }}>
            <Typography sx={{ fontWeight: "bold", color: "darkBlue.light" }}>Customers</Typography>
         </Box>
         <Box sx={{ height: 320, overflow: "auto" }}>
            <AutoSizer>
               {({ height, width }) => (
                  <InfiniteLoader
                     isItemLoaded={isItemLoaded}
                     itemCount={filteredCustomers.length + 20}
                     loadMoreItems={loadMoreItems}
                  >
                     {({ onItemsRendered, ref }) => (
                        <List
                           height={height}
                           width={width}
                           itemCount={filteredCustomers.length}
                           itemSize={60}
                           itemKey={(index) => filteredCustomers[index]?.customerID || index}
                           itemData={filteredCustomers}
                           outerElementType="div"
                           onItemsRendered={onItemsRendered}
                           ref={ref}
                        >
                           {Row}
                        </List>
                     )}
                  </InfiniteLoader>
               )}
            </AutoSizer>
         </Box>
      </Box>
   );
}
